import axios from 'axios'
import { computed, defineComponent, reactive, ref, watch } from 'vue'
import { string } from 'zod'

import { CsCopyTextLink, CsInputField, CsModal, CsPrimaryButton, CsRadioButton, CsTag } from '@sunrise/components'
import { storeToRefs } from '@sunrise/pinia'
import { screenSizeDetection, useGlobalStore } from '@sunrise/shared'
import { TrackingParameter, track } from '@sunrise/tracking'

import { buildMailAgentSearchText } from '../../ts/shared/mailAgentBuilder/mailAgentBuilder'
import { useMailAgentStore } from '../../ts/store/mailAgentStoreModule/mailAgentStoreModule'
import { useResultListStore } from '../../ts/store/resultListStoreModule/resultListStoreModule'

import MsNudge from '../MsNudge/MsNudge.vue'
import {
    MailAgentIntervalOptionsLabel,
    MailAgentIntervalOptionsValue,
    MailAgentRequestParams,
    MailAgentTrackingVariant,
} from './MsMailAgentModal.enum'
import { MailAgentFormData, MailAgentIntervalOptions } from './MsMailAgentModal.interface'
import { buildStaticTealiumDataSendForm, buildTealiumDataView } from './MsMailAgentModalTracking'
import MsFormSuccessMailAgent from './formSuccessMailAgent.svg?component'

export default defineComponent({
    name: 'MsMailAgentModal',
    components: {
        CsCopyTextLink,
        CsModal,
        CsTag,
        CsRadioButton,
        CsInputField,
        CsPrimaryButton,
        MsFormSuccessMailAgent,
        MsNudge,
    },
    props: {
        isVisible: {
            type: Boolean,
            required: true,
        },
        trackingVariant: {
            type: String as () => MailAgentTrackingVariant,
            required: true,
        },
    },
    emits: ['closeModal'],
    setup(props, { emit }) {
        const { isScreenM } = screenSizeDetection()
        const globalStore = useGlobalStore()
        const mailAgentStore = useMailAgentStore()
        const privacyPoliceUrl = globalStore.requestContext?.globalLinks.datenschutzerklaerung
        const servicePath = globalStore.urlPaths?.services
        const SERVICE_URL = `${servicePath}/mail-agent-subscription`
        const emailSchema = string().email()
        const tags = computed(() => mailAgentStore.tagsAsArray)
        const { searchParams } = storeToRefs(useResultListStore())
        const showErrorMessage = ref<boolean>(false)
        const showSuccessView = ref<boolean>(false)
        const showNudge = ref<boolean>(false)
        const showedNudgeAlready = ref<boolean>(false)
        const intervalOptions: MailAgentIntervalOptions = {
            hourly: {
                label: MailAgentIntervalOptionsLabel.HOURLY,
                value: MailAgentIntervalOptionsValue.HOURLY,
            },
            every4hours: {
                label: MailAgentIntervalOptionsLabel.EVERY4HOURS,
                value: MailAgentIntervalOptionsValue.EVERY4HOURS,
            },
            daily: {
                label: MailAgentIntervalOptionsLabel.DAILY,
                value: MailAgentIntervalOptionsValue.DAILY,
            },
        }
        const formData: MailAgentFormData = reactive({
            interval: {
                label: MailAgentIntervalOptionsLabel.HOURLY,
                value: MailAgentIntervalOptionsValue.HOURLY,
            },
            email: '',
        })

        function emitClosedModalEvent(): void {
            emit('closeModal')
            showSuccessView.value = false
            showNudge.value = false
            showedNudgeAlready.value = false
        }

        function onSubmit(): void {
            if (!emailSchema.safeParse(formData.email).success) {
                showErrorMessage.value = true
                return
            }

            const searchText: string = buildMailAgentSearchText(mailAgentStore.tags)

            showErrorMessage.value = false

            if (searchParams) {
                sendFormData(formData, { ...searchParams.value, searchText })
                trackSubmit()
            }
        }

        function sendFormData(formData: MailAgentFormData, mailAgentRequestParams: MailAgentRequestParams): void {
            axios
                .post(SERVICE_URL, { formData, mailAgentRequestParams })
                .then(successHandler)
                .catch(function (error) {
                    console.error(error)
                })
        }

        function successHandler(): void {
            showSuccessView.value = true
            showErrorMessage.value = false
        }

        watch(
            () => props.isVisible,
            (visible) => {
                if (visible) {
                    trackView()
                }
            },
        )

        function trackView(): void {
            const tealiumData: TrackingParameter = buildTealiumDataView(props.trackingVariant)
            track(tealiumData)
        }

        function trackSubmit(): void {
            const tealiumData: TrackingParameter = buildStaticTealiumDataSendForm(props.trackingVariant)
            track(tealiumData)
        }

        function onChangeInterval(answer: MailAgentIntervalOptionsValue): void {
            if (answer != MailAgentIntervalOptionsValue.HOURLY && !showedNudgeAlready.value) {
                showNudge.value = true
                showedNudgeAlready.value = true
            }
            formData.interval.value = answer
        }

        function closeNudge(): void {
            showNudge.value = false
        }

        return {
            isScreenM,
            showErrorMessage,
            showSuccessView,
            showNudge,
            formData,
            intervalOptions,
            privacyPoliceUrl,
            tags,
            emitClosedModalEvent,
            onSubmit,
            onChangeInterval,
            closeNudge,
        }
    },
})
